import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, onSnapshot } from "firebase/firestore";
import { ensureCompleteUserDocument } from '../utils/userHelpers';
import { syncPreferredLanguageFromCloud } from '../utils/bilingualResolver';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to sign in with Google
  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Create or update user document with ALL required fields
      // Non-blocking: don't let Firestore errors break the login flow
      try {
        await ensureCompleteUserDocument(user);
      } catch (docError) {
        console.warn("User doc setup failed (non-blocking):", docError);
      }

    } catch (error) {
      console.error("Error during Google sign-in:", error);
      throw error;
    }
  };

  const logout = () => {
    setUserData(null);
    return signOut(auth);
  };

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) {
        setUserData(null);
        setLoading(false);
      }
    });
    return unsubscribeAuth;
  }, []);

  useEffect(() => {
    let unsubscribeFirestore;
    if (currentUser) {
      const userRef = doc(db, "users", currentUser.uid);
      unsubscribeFirestore = onSnapshot(userRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserData(data);
          // Pull the account's content-language preference into this browser
          syncPreferredLanguageFromCloud(data);
        } else {
          console.log("User document not found yet.");
        }
        setLoading(false);
      });
    }
    return () => {
      if (unsubscribeFirestore) {
        unsubscribeFirestore();
      }
    };
  }, [currentUser]);

  const value = {
    currentUser,
    userData,
    loading,
    logout,
    signInWithGoogle, // Expose the new function
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}